import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

/**
 * ReadinessRing — animated SVG ring that counts 0 → target on mount.
 * Designed for the hero overlay. Crystal-glass surround handled by caller.
 */
export default function ReadinessRing({ target = 87, size = 156, label = 'READINESS' }) {
  const [score, setScore] = useState(0);
  const radius = (size - 24) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;

  useEffect(() => {
    const start = performance.now();
    const dur = 1700;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setScore(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  const stroke = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center" data-testid="landing-readiness-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="rgba(15, 23, 42, 0.08)" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={stroke} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.12s linear, stroke 0.3s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="font-mono font-bold tabular-nums leading-none text-slate-900"
          style={{ fontSize: size * 0.30 }}>
          {score}
        </motion.span>
        <span className="text-[9px] font-medium uppercase tracking-[0.2em] text-slate-500 mt-1 font-mono">
          {label}
        </span>
      </div>
    </div>
  );
}
